import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import PlanService, { PlanFormPayload } from '../../../../services/plan.service';
import ProductService from '../../../../services/product.service';
import MasterLookupService from '../../../../services/master-lookup.service';
import MasterService from '../../../../services/master.service';
import { Modules, AuthRoutes } from '../../../../enums';

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

interface BillingCycleOption {
  id: number;
  cycle_code: string;
  cycle_name: string;
  duration_in_months: number;
}

interface CurrencyOption {
  id: number;
  currency_code: string;
  currency_name: string;
  symbol: string;
}

interface StatusOption {
  id: number;
  status_name: string;
  colour_code: string;
}

interface PlanFormState {
  plan_code: string;
  plan_name: string;
  product_id: number | '';
  billing_cycle_id: number | '';
  price: number | string;
  currency_id: number | '';
  description: string;
  entitlements: string[];
  trial_days: number | string;
  status_id: number | '';
  notes: string;
}

const initialForm: PlanFormState = {
  plan_code: '',
  plan_name: '',
  product_id: '',
  billing_cycle_id: '',
  price: '',
  currency_id: '',
  description: '',
  entitlements: [],
  trial_days: 0,
  status_id: '',
  notes: '',
};

const PlanForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<PlanFormState>(initialForm);
  const [entitlementInput, setEntitlementInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [billingCycles, setBillingCycles] = useState<BillingCycleOption[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
    MasterLookupService.list({ type: 'billingCycles', includeDeleted: false, limit: 100 }).then((response: any) => {
      setBillingCycles(response?.data?.items ?? []);
    });
    MasterLookupService.list({ type: 'currencies', includeDeleted: false, limit: 100 }).then((response: any) => {
      setCurrencies(response?.data?.items ?? []);
    });
    MasterService.list({ type: 'subscriptionStatuses', includeDeleted: false, limit: 100 }).then((response: any) => {
      setStatuses(response?.data?.items ?? []);
    });
  }, []);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    PlanService.get(id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          plan_code: row.plan_code ?? '',
          plan_name: row.plan_name ?? '',
          product_id: row.product_id ?? '',
          billing_cycle_id: row.billing_cycle_id ?? '',
          price: row.price ?? '',
          currency_id: row.currency_id ?? '',
          description: row.description ?? '',
          entitlements: row.entitlements ?? [],
          trial_days: row.trial_days ?? 0,
          status_id: row.status_id ?? '',
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const selectedCycleDuration = useMemo(() => {
    const cycle = billingCycles.find((c) => c.id === formData.billing_cycle_id);
    return cycle ? cycle.duration_in_months : null;
  }, [billingCycles, formData.billing_cycle_id]);

  const validate = useCallback(
    (data: PlanFormState) => {
      const nextErrors: Record<string, string> = {};
      if (!data.plan_code.trim()) nextErrors.plan_code = t('masters.plans.form.pageTexts.fields.planCode.errors.required');
      if (!data.plan_name.trim()) nextErrors.plan_name = t('masters.plans.form.pageTexts.fields.planName.errors.required');
      if (!data.product_id) nextErrors.product_id = t('masters.plans.form.pageTexts.fields.product.errors.required');
      if (!data.billing_cycle_id) nextErrors.billing_cycle_id = t('masters.plans.form.pageTexts.fields.billingCycle.errors.required');
      if (!data.currency_id) nextErrors.currency_id = t('masters.plans.form.pageTexts.fields.currency.errors.required');
      const price = Number(data.price);
      if (!data.price || !(price > 0)) nextErrors.price = t('masters.plans.form.pageTexts.fields.price.errors.invalid');
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const numericFields = ['product_id', 'billing_cycle_id', 'currency_id', 'status_id'];
    const value = numericFields.includes(name) ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleEntitlementKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const value = entitlementInput.trim();
        if (value && !formData.entitlements.includes(value)) {
          setFormData((prev) => ({ ...prev, entitlements: [...prev.entitlements, value] }));
        }
        setEntitlementInput('');
      }
    },
    [entitlementInput, formData.entitlements]
  );

  const removeEntitlement = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, entitlements: prev.entitlements.filter((e) => e !== value) }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload: PlanFormPayload = {
          plan_code: formData.plan_code,
          plan_name: formData.plan_name,
          product_id: Number(formData.product_id),
          billing_cycle_id: Number(formData.billing_cycle_id),
          price: Number(formData.price),
          currency_id: Number(formData.currency_id),
          description: formData.description || undefined,
          entitlements: formData.entitlements,
          trial_days: Number(formData.trial_days) || 0,
          status_id: formData.status_id ? Number(formData.status_id) : undefined,
          notes: formData.notes || undefined,
        };

        if (edit && id) {
          await PlanService.update(id, payload);
          toast.success(<Toast message={t('masters.plans.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await PlanService.create(payload);
          toast.success(<Toast message={t('masters.plans.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.masters}${AuthRoutes.plans}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('masters.plans.form.pageTexts.editTitle') : t('masters.plans.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-3">
          <Input
            id="plan_code"
            name="plan_code"
            label={t('masters.plans.form.pageTexts.fields.planCode.label')}
            placeholder={t('masters.plans.form.pageTexts.fields.planCode.placeholder')}
            required
            value={formData.plan_code}
            error={!!errors.plan_code}
            errorMessage={errors.plan_code}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="plan_name"
            name="plan_name"
            label={t('masters.plans.form.pageTexts.fields.planName.label')}
            placeholder={t('masters.plans.form.pageTexts.fields.planName.placeholder')}
            required
            value={formData.plan_name}
            error={!!errors.plan_name}
            errorMessage={errors.plan_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-5">
          <label className="form-label d-block" htmlFor="product_id">
            {t('masters.plans.form.pageTexts.fields.product.label')}
          </label>
          <select
            id="product_id"
            name="product_id"
            className={`form-select ${errors.product_id ? 'is-invalid' : ''}`}
            value={formData.product_id}
            onChange={(e) => handleChange(e, 'product_id')}
          >
            <option value="">{t('masters.plans.form.pageTexts.fields.product.placeholder')}</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.product_code} — {product.product_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="billing_cycle_id">
            {t('masters.plans.form.pageTexts.fields.billingCycle.label')}
          </label>
          <select
            id="billing_cycle_id"
            name="billing_cycle_id"
            className={`form-select ${errors.billing_cycle_id ? 'is-invalid' : ''}`}
            value={formData.billing_cycle_id}
            onChange={(e) => handleChange(e, 'billing_cycle_id')}
          >
            <option value="">{t('masters.plans.form.pageTexts.fields.billingCycle.placeholder')}</option>
            {billingCycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.cycle_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label d-block">{t('masters.plans.form.pageTexts.fields.duration.label')}</label>
          <input
            className="form-control"
            value={selectedCycleDuration !== null ? t('masters.plans.form.pageTexts.fields.duration.value', { count: selectedCycleDuration }) : ''}
            disabled
            readOnly
          />
        </div>
        <div className="col-md-3">
          <Input
            id="price"
            name="price"
            type="number"
            min="0.01"
            label={t('masters.plans.form.pageTexts.fields.price.label')}
            required
            value={formData.price}
            error={!!errors.price}
            errorMessage={errors.price}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label d-block" htmlFor="currency_id">
            {t('masters.plans.form.pageTexts.fields.currency.label')}
          </label>
          <select
            id="currency_id"
            name="currency_id"
            className={`form-select ${errors.currency_id ? 'is-invalid' : ''}`}
            value={formData.currency_id}
            onChange={(e) => handleChange(e, 'currency_id')}
          >
            <option value="">{t('masters.plans.form.pageTexts.fields.currency.placeholder')}</option>
            {currencies.map((currency) => (
              <option key={currency.id} value={currency.id}>
                {currency.currency_code} ({currency.symbol})
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label d-block" htmlFor="status_id">
            {t('masters.plans.form.pageTexts.fields.status.label')}
          </label>
          <select
            id="status_id"
            name="status_id"
            className="form-select"
            value={formData.status_id}
            onChange={(e) => handleChange(e, 'status_id')}
          >
            <option value="">{t('masters.plans.form.pageTexts.fields.status.placeholder')}</option>
            {statuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.status_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <Input
            id="trial_days"
            name="trial_days"
            type="number"
            min="0"
            label={t('masters.plans.form.pageTexts.fields.trialDays.label')}
            value={formData.trial_days}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6">
          <Input
            id="description"
            name="description"
            label={t('masters.plans.form.pageTexts.fields.description.label')}
            placeholder={t('masters.plans.form.pageTexts.fields.description.placeholder')}
            value={formData.description}
            onChange={handleChange}
          />
        </div>

        <div className="col-12">
          <label className="form-label d-block" htmlFor="entitlementInput">
            {t('masters.plans.form.pageTexts.fields.entitlements.label')}
          </label>
          <input
            id="entitlementInput"
            className="form-control mb-2"
            placeholder={t('masters.plans.form.pageTexts.fields.entitlements.placeholder')}
            value={entitlementInput}
            onChange={(e) => setEntitlementInput(e.target.value)}
            onKeyDown={handleEntitlementKeyDown}
          />
          <div className="d-flex flex-wrap gap-2">
            {formData.entitlements.map((value) => (
              <span key={value} className="badge bg-label-primary d-flex align-items-center gap-1">
                {value}
                <button
                  type="button"
                  className="btn-close btn-close-sm"
                  style={{ fontSize: 8 }}
                  onClick={() => removeEntitlement(value)}
                  aria-label="remove"
                />
              </span>
            ))}
          </div>
        </div>

        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('masters.plans.form.pageTexts.fields.notes.label')}
            placeholder={t('masters.plans.form.pageTexts.fields.notes.placeholder')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>

        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('masters.plans.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.plans}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('masters.plans.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default PlanForm;
